import { ContentType } from '../graphql/generated/hasura';
import heic2any from 'heic2any';

export const mapFileTypeToApiFileType: Record<string, ContentType> = {
  // Document Types
  'application/pdf': ContentType.Pdf,
  'application/msword': ContentType.Doc,
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    ContentType.Docx,

  // Image Types
  'image/jpeg': ContentType.Jpeg,
  'image/jpg': ContentType.Jpg,
  'image/png': ContentType.Png,
  'image/gif': ContentType.Gif,
  'image/heif': ContentType.Heif,
  'image/heic': ContentType.Heic,
  'image/webp': ContentType.Webp,

  // Video
  'video/mp4': ContentType.Mp4,

  // Audio
  'audio/mpeg': ContentType.Mp3,

  // Data & Spreadsheet Types
  'text/csv': ContentType.Csv,
  'application/json': ContentType.Json,
  'application/vnd.ms-excel': ContentType.Xls,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
    ContentType.Xlsx,
  'text/plain': ContentType.Txt,
};

export const createAcceptPolicy = (allowedTypes: ContentType[]) => {
  const acceptPolicy: { [key: string]: string[] } = {};

  for (const mimeType in mapFileTypeToApiFileType) {
    const contentType = mapFileTypeToApiFileType[mimeType] as ContentType;
    if (allowedTypes.includes(contentType)) {
      const extension = `.${contentType.toLowerCase()}` ?? '.png';
      acceptPolicy[mimeType] = [extension];
    }
  }

  return acceptPolicy;
};

export const mapApiFileTypeToFileType: Record<string, string> = Object.entries(
  mapFileTypeToApiFileType,
).reduce((acc, [key, value]) => {
  if (!acc[value]) {
    acc[value] = key;
  }
  return acc;
}, {} as Record<string, string>);

export const mapApiFileType: Record<string, string> = {
  [ContentType.Pdf]: 'PDF',
  [ContentType.Doc]: 'DOC',
  [ContentType.Docx]: 'DOCX',
  [ContentType.Jpeg]: 'JPG',
  [ContentType.Jpg]: 'JPG',
  [ContentType.Png]: 'PNG',
  [ContentType.Heif]: 'HEIF',
  [ContentType.Heic]: 'HEIC',
  [ContentType.Gif]: 'GIF',
  [ContentType.Xls]: 'XLS',
  [ContentType.Xlsx]: 'XLSX',
};

export const formatFileType = (mimeType: string): string => {
  return (
    mapFileTypeToApiFileType[mimeType] ||
    mimeType?.split('/')?.[1]?.toUpperCase()
  ); // fallback
};

export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  } else {
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }
};

export const downloadFileFromUrl = async (
  fileUrl: string,
  filename = 'file',
): Promise<void> => {
  if (!fileUrl) {
    console.warn('No URL provided for download');
    return;
  }

  try {
    const response = await fetch(fileUrl, {
      method: 'GET',
      mode: 'cors',
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch file: ${response.status}`);
    }

    const blob = await response.blob();
    const mime = blob.type;
    const ext = mime && mime.includes('/') ? mime.split('/')[1] : 'bin';

    const safeFilename = filename.includes(`.${ext}`)
      ? filename
      : `${filename}.${ext}`;

    const blobUrl = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', safeFilename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('File download failed:', error);
  }
};

export async function saveFile(response: Response, filename: string) {
  const blob = await response.blob();
  const mime = blob.type;
  const ext = mime && mime.includes('/') ? mime.split('/')[1] : 'bin';

  const safeFilename = filename.includes(`.${ext}`)
    ? filename
    : `${filename}.${ext}`;

  const blobUrl = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = blobUrl;
  link.setAttribute('download', safeFilename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(blobUrl);
}

export function openPdfOnMobileSafe(pdfLink: string) {
  if (!pdfLink) return;
  const link = document.createElement('a');
  link.href = pdfLink;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function hasHeicFile(file: File | null): boolean {
  if (!file) return false;
  return (
    file.type.toLowerCase() === 'image/heic' ||
    file.type.toLowerCase() === 'image/heif' ||
    file.name.toLowerCase().endsWith('.heic')
  );
}

export async function convertHeicToJpg(file: File) {
  const convertedBlob = await heic2any({
    blob: file,
    toType: 'image/jpeg',
    quality: 1,
  });

  const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
  return new File([blob], file.name.replace(/\.(heic|heif)$/i, '.jpg'), {
    type: 'image/jpeg',
  });
}
